/**
 * Firm-side permission catalog — the canonical pool driving route enforcement.
 * String values are `domain:action`. This enum is the single source of truth for
 * `requirePermissions()` gates; `PERMISSION_CATALOG` mirrors it with structural metadata
 * served by `GET /permissions` (read by the phase-2 role composer UI).
 *
 * Display copy (label/description) is NOT stored here or in the DB — the frontend
 * looks it up via `t(\`permissions.${key}.label\`)` in en.json/he.json, same as every
 * other user-visible string in the app.
 */
export enum Permission {
  TRUST_ACCOUNTS_READ = 'trust_accounts:read',
  TRUST_ACCOUNTS_CREATE = 'trust_accounts:create',
  TRUST_ACCOUNTS_UPDATE = 'trust_accounts:update',
  TRUST_ACCOUNTS_SIGN_DECLARATIONS = 'trust_accounts:sign_declarations',
  TRUST_ACCOUNTS_DELETE = 'trust_accounts:delete',
  TRUST_ACCOUNTS_SUBMIT = 'trust_accounts:submit',
  TRUST_ACCOUNTS_MANAGE_MEMBERS = 'trust_accounts:manage_members',

  BUYERS_READ = 'buyers:read',
  BUYERS_CREATE = 'buyers:create',
  BUYERS_UPDATE = 'buyers:update',
  BUYERS_DELETE = 'buyers:delete',

  BENEFICIARIES_CREATE = 'beneficiaries:create',
  BENEFICIARIES_UPDATE = 'beneficiaries:update',
  BENEFICIARIES_DELETE = 'beneficiaries:delete',

  PAYMENTS_READ = 'payments:read',
  PAYMENTS_COLLECT = 'payments:collect',
  PAYMENTS_RESEND = 'payments:resend',
  PAYMENTS_PAYOUT = 'payments:payout',

  DOCUMENTS_READ = 'documents:read',
  DOCUMENTS_UPLOAD = 'documents:upload',
  DOCUMENTS_DELETE = 'documents:delete',
  DOCUMENTS_REVIEW_EXTRACTION = 'documents:review_extraction',

  TEAM_READ = 'team:read',
  TEAM_MANAGE_MEMBERS = 'team:manage_members',
  TEAM_INVITE_MEMBERS = 'team:invite_members',

  FIRM_SETTINGS_UPDATE = 'firm_settings:update'
}

/** Domain grouping for the permission catalog — drives UI grouping in the (later) role composer. */
export enum PermissionDomain {
  TRUST_ACCOUNTS = 'trust_accounts',
  BUYERS = 'buyers',
  BENEFICIARIES = 'beneficiaries',
  PAYMENTS = 'payments',
  DOCUMENTS = 'documents',
  TEAM = 'team',
  FIRM_SETTINGS = 'firm_settings'
}

/** Structural metadata for a single permission. No display copy — see the module doc comment. */
export interface PermissionCatalogEntry {
  key: Permission;
  domain: PermissionDomain;
  action: string;
  /** Drives the "sensitive action" badge + count in the role composer UI. */
  isSensitive: boolean;
  sortOrder: number;
}

/**
 * Canonical firm-side permission catalog — 7 domains, 26 permissions. Adding a new
 * permission means adding both a `Permission` value and a matching entry here.
 */
export const PERMISSION_CATALOG: readonly PermissionCatalogEntry[] = [
  {
    key: Permission.TRUST_ACCOUNTS_READ,
    domain: PermissionDomain.TRUST_ACCOUNTS,
    action: 'read',
    isSensitive: false,
    sortOrder: 1
  },
  {
    key: Permission.TRUST_ACCOUNTS_CREATE,
    domain: PermissionDomain.TRUST_ACCOUNTS,
    action: 'create',
    isSensitive: false,
    sortOrder: 2
  },
  {
    key: Permission.TRUST_ACCOUNTS_UPDATE,
    domain: PermissionDomain.TRUST_ACCOUNTS,
    action: 'update',
    isSensitive: false,
    sortOrder: 3
  },
  {
    key: Permission.TRUST_ACCOUNTS_SIGN_DECLARATIONS,
    domain: PermissionDomain.TRUST_ACCOUNTS,
    action: 'sign_declarations',
    isSensitive: true,
    sortOrder: 4
  },
  {
    key: Permission.TRUST_ACCOUNTS_DELETE,
    domain: PermissionDomain.TRUST_ACCOUNTS,
    action: 'delete',
    isSensitive: true,
    sortOrder: 5
  },
  {
    key: Permission.TRUST_ACCOUNTS_SUBMIT,
    domain: PermissionDomain.TRUST_ACCOUNTS,
    action: 'submit',
    isSensitive: true,
    sortOrder: 6
  },
  {
    key: Permission.TRUST_ACCOUNTS_MANAGE_MEMBERS,
    domain: PermissionDomain.TRUST_ACCOUNTS,
    action: 'manage_members',
    isSensitive: true,
    sortOrder: 7
  },

  {
    key: Permission.BUYERS_READ,
    domain: PermissionDomain.BUYERS,
    action: 'read',
    isSensitive: false,
    sortOrder: 8
  },
  {
    key: Permission.BUYERS_CREATE,
    domain: PermissionDomain.BUYERS,
    action: 'create',
    isSensitive: false,
    sortOrder: 9
  },
  {
    key: Permission.BUYERS_UPDATE,
    domain: PermissionDomain.BUYERS,
    action: 'update',
    isSensitive: false,
    sortOrder: 10
  },
  {
    key: Permission.BUYERS_DELETE,
    domain: PermissionDomain.BUYERS,
    action: 'delete',
    isSensitive: true,
    sortOrder: 11
  },

  {
    key: Permission.BENEFICIARIES_CREATE,
    domain: PermissionDomain.BENEFICIARIES,
    action: 'create',
    isSensitive: false,
    sortOrder: 12
  },
  {
    key: Permission.BENEFICIARIES_UPDATE,
    domain: PermissionDomain.BENEFICIARIES,
    action: 'update',
    isSensitive: false,
    sortOrder: 13
  },
  {
    key: Permission.BENEFICIARIES_DELETE,
    domain: PermissionDomain.BENEFICIARIES,
    action: 'delete',
    isSensitive: true,
    sortOrder: 14
  },

  {
    key: Permission.PAYMENTS_READ,
    domain: PermissionDomain.PAYMENTS,
    action: 'read',
    isSensitive: false,
    sortOrder: 15
  },
  {
    key: Permission.PAYMENTS_COLLECT,
    domain: PermissionDomain.PAYMENTS,
    action: 'collect',
    isSensitive: true,
    sortOrder: 16
  },
  {
    key: Permission.PAYMENTS_RESEND,
    domain: PermissionDomain.PAYMENTS,
    action: 'resend',
    isSensitive: false,
    sortOrder: 17
  },
  {
    key: Permission.PAYMENTS_PAYOUT,
    domain: PermissionDomain.PAYMENTS,
    action: 'payout',
    isSensitive: true,
    sortOrder: 18
  },

  {
    key: Permission.DOCUMENTS_READ,
    domain: PermissionDomain.DOCUMENTS,
    action: 'read',
    isSensitive: false,
    sortOrder: 19
  },
  {
    key: Permission.DOCUMENTS_UPLOAD,
    domain: PermissionDomain.DOCUMENTS,
    action: 'upload',
    isSensitive: false,
    sortOrder: 20
  },
  {
    key: Permission.DOCUMENTS_DELETE,
    domain: PermissionDomain.DOCUMENTS,
    action: 'delete',
    isSensitive: true,
    sortOrder: 21
  },
  {
    key: Permission.DOCUMENTS_REVIEW_EXTRACTION,
    domain: PermissionDomain.DOCUMENTS,
    action: 'review_extraction',
    isSensitive: false,
    sortOrder: 22
  },

  {
    key: Permission.TEAM_READ,
    domain: PermissionDomain.TEAM,
    action: 'read',
    isSensitive: false,
    sortOrder: 23
  },
  {
    key: Permission.TEAM_MANAGE_MEMBERS,
    domain: PermissionDomain.TEAM,
    action: 'manage_members',
    isSensitive: true,
    sortOrder: 24
  },
  {
    key: Permission.TEAM_INVITE_MEMBERS,
    domain: PermissionDomain.TEAM,
    action: 'invite_members',
    isSensitive: true,
    sortOrder: 25
  },

  {
    key: Permission.FIRM_SETTINGS_UPDATE,
    domain: PermissionDomain.FIRM_SETTINGS,
    action: 'update',
    isSensitive: true,
    sortOrder: 26
  }
] as const;
